/**
 * Панель фильтров над шапкой таблицы (§10.1, §10.2).
 *
 * Все фильтры живут здесь и только здесь: отдельного выпадающего окна со
 * списком блоков больше нет — оно дублировало эту панель и называло те же
 * значения другими словами.
 *
 * Две строки. Верхняя — фильтры по значениям: каждый чип открывает свой
 * список со счётчиками и мультивыбором, выбранное видно прямо в чипе теми же
 * плашками, что в таблице. Нижняя — быстрые фильтры: готовые условия вроде
 * «Без URL», которые включаются одним нажатием и сочетаются друг с другом.
 */
import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  Checkbox, IconChevronDown, IconClose, IconSearch, Input, Popover, TierBadge, chipColor,
} from '../../../components/index.js'
import { useApp } from '../../../store/app-store.js'
import {
  CHANGED_PERIODS, flagChipState, groupChipState, UNSET, withFlag, withGroupValue,
  type ValueGroupKey, type WhiteGloveField,
} from './apply.js'
import type { BrandFiltersState } from './useBrandFilters.js'
import { filterTexts } from './texts.filters.js'
import './FilterPills.css'

export interface FilterPillsProps {
  state: BrandFiltersState
}

type Counts = Record<string, number>

/** Описание чипа-группы: откуда значения, как их звать и чем рисовать. */
interface GroupSpec {
  key: ValueGroupKey
  title: string
  enumSet: string
  counts: (chips: BrandFiltersState['chips']) => Counts
  badge: 'tier' | 'enum'
}

const GROUPS: readonly GroupSpec[] = [
  { key: 'tiers', title: filterTexts.pillGroups.tier, enumSet: 'tier', counts: (c) => c.tiers, badge: 'tier' },
  { key: 'visibility', title: filterTexts.pillGroups.visibility, enumSet: 'visibility', counts: (c) => c.visibility, badge: 'enum' },
  { key: 'seoPriority', title: filterTexts.pillGroups.seo, enumSet: 'seo_priority', counts: (c) => c.seoPriority, badge: 'enum' },
  { key: 'contextPriority', title: filterTexts.pillGroups.context, enumSet: 'context_priority', counts: (c) => c.contextPriority, badge: 'enum' },
  { key: 'stockDisplay', title: filterTexts.panel.stockDisplay, enumSet: 'stock_display', counts: (c) => c.stockDisplay, badge: 'enum' },
  { key: 'oos', title: filterTexts.panel.oos, enumSet: 'oos', counts: (c) => c.oos, badge: 'enum' },
]

const WHITE_GLOVE: readonly [WhiteGloveField, string][] = [
  ['delivery', filterTexts.panel.wgDelivery],
  ['installation', filterTexts.panel.wgInstallation],
  ['warranty', filterTexts.panel.wgWarranty],
]

export function FilterPills({ state }: FilterPillsProps) {
  const bootstrap = useApp((s) => s.bootstrap)
  const { filters, chips, setFilters } = state
  const enums = bootstrap?.enums ?? {}
  const users = bootstrap?.users ?? []

  const period = filters.changedWithinDays ?? CHANGED_PERIODS[0]

  /** «Не задан» всегда последним: это не значение, а его отсутствие. */
  const optionsOf = useMemo(() => (spec: GroupSpec) => {
    const values = [...(enums[spec.enumSet] ?? [])]
      .filter((item) => item.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((item) => ({ value: item.value as string | null, label: item.label }))
    values.push({ value: UNSET, label: filterTexts.pillGroups.empty })
    return values
  }, [enums])

  return (
    <div className="pills" id="bm-filters" role="group" aria-label={filterTexts.pills.label}>
      <div className="pills__row">
        <span className="pills__title t-label">{filterTexts.pillGroups.title}</span>

        <div className="pills__scroll">
          {/* Канал присутствия — один из вариантов, а не набор. */}
          <ChoicePill
            title={filterTexts.panel.presentIn}
            value={filters.presentIn ?? 'any'}
            options={[
              { value: 'any', label: filterTexts.panel.any },
              { value: 'retail', label: filterTexts.panel.presentRetail, count: chips.presentIn.retail },
              { value: 'b2b', label: filterTexts.panel.presentB2b, count: chips.presentIn.b2b },
              { value: 'only_one', label: filterTexts.panel.presentOnlyOne, count: chips.presentIn.only_one },
              { value: 'both', label: filterTexts.panel.presentBoth, count: chips.presentIn.both },
            ]}
            onPick={(value) => {
              const next = { ...filters }
              if (value === 'any') delete next.presentIn
              else next.presentIn = value as typeof filters.presentIn
              setFilters(next)
            }}
          />

          {GROUPS.map((spec) => (
            <GroupPill
              key={spec.key}
              spec={spec}
              options={optionsOf(spec)}
              counts={spec.counts(chips)}
              state={state}
            />
          ))}

          <ChoicePill
            title={filterTexts.panel.url}
            value={filters.hasUrl === undefined ? 'any' : filters.hasUrl ? 'yes' : 'no'}
            options={[
              { value: 'any', label: filterTexts.panel.urlAny },
              { value: 'yes', label: filterTexts.panel.urlFilled, count: chips.hasUrl },
              { value: 'no', label: filterTexts.panel.urlEmpty, count: chips.noUrl },
            ]}
            onPick={(value) => {
              const next = { ...filters }
              if (value === 'any') delete next.hasUrl
              else next.hasUrl = value === 'yes'
              setFilters(next)
            }}
          />

          <CategoryPill state={state} />

          {/* White Glove: чекбоксы «заполнено» по трём полям сразу. */}
          <ChecksPill
            title={filterTexts.panel.whiteGlove}
            items={WHITE_GLOVE.map(([field, label]) => ({
              id: field,
              label,
              count: chips.whiteGlove[field],
              on: (filters.whiteGlove ?? []).includes(field),
            }))}
            onToggle={(id) => {
              const current = filters.whiteGlove ?? []
              const next = current.includes(id as WhiteGloveField)
                ? current.filter((item) => item !== id)
                : [...current, id as WhiteGloveField]
              const value = { ...filters }
              if (next.length === 0) delete value.whiteGlove
              else value.whiteGlove = next
              setFilters(value)
            }}
            onClear={() => {
              const next = { ...filters }
              delete next.whiteGlove
              setFilters(next)
            }}
          />

          <ChecksPill
            title={filterTexts.panel.changedBy}
            items={users
              .filter((user) => (chips.authors[user.id] ?? 0) > 0)
              .map((user) => ({
                id: String(user.id),
                label: user.shortName,
                count: chips.authors[user.id] ?? 0,
                on: (filters.changedBy ?? []).includes(user.id),
              }))}
            onToggle={(id) => {
              const authorId = Number(id)
              const current = filters.changedBy ?? []
              const next = current.includes(authorId)
                ? current.filter((item) => item !== authorId)
                : [...current, authorId]
              const value = { ...filters }
              if (next.length === 0) delete value.changedBy
              else value.changedBy = next
              setFilters(value)
            }}
            onClear={() => {
              const next = { ...filters }
              delete next.changedBy
              setFilters(next)
            }}
          />

          {/* ── комментарии и задачи ──
              По ним ведут работу: «что писал Дмитрий», «где давно не писали»,
              «где висит незакрытая задача». */}
          <ChecksPill
            title={filterTexts.panel.commentBy}
            items={users
              .filter((user) => (chips.commentAuthors[user.id] ?? 0) > 0)
              .map((user) => ({
                id: String(user.id),
                label: user.shortName,
                count: chips.commentAuthors[user.id] ?? 0,
                on: (filters.commentBy ?? []).includes(user.id),
              }))}
            onToggle={(id) => {
              const authorId = Number(id)
              const current = filters.commentBy ?? []
              const next = current.includes(authorId)
                ? current.filter((item) => item !== authorId)
                : [...current, authorId]
              const value = { ...filters }
              if (next.length === 0) delete value.commentBy
              else value.commentBy = next
              setFilters(value)
            }}
            onClear={() => {
              const next = { ...filters }
              delete next.commentBy
              setFilters(next)
            }}
          />

          {/* Свои столбцы: текстовым хватает «заполнено · пусто», списки и
              числа фильтруются своими значениями в будущем. */}
          {(bootstrap?.fields ?? [])
            .filter((field) => field.status === 'active' && field.type === 'text')
            .map((field) => {
              const current = filters.fields?.[field.code] as { kind?: string; filled?: boolean } | undefined
              const value = current?.kind === 'text' ? (current.filled ? 'yes' : 'no') : 'any'
              return (
                <ChoicePill
                  key={field.code}
                  title={field.name}
                  value={value}
                  options={[
                    { value: 'any', label: filterTexts.panel.any },
                    { value: 'yes', label: filterTexts.panel.fieldFilled },
                    { value: 'no', label: filterTexts.panel.fieldEmpty },
                  ]}
                  onPick={(picked) => {
                    const fields = { ...(filters.fields ?? {}) }
                    if (picked === 'any') delete fields[field.code]
                    else fields[field.code] = { kind: 'text', filled: picked === 'yes' }
                    const next = { ...filters }
                    if (Object.keys(fields).length === 0) delete next.fields
                    else next.fields = fields
                    setFilters(next)
                  }}
                />
              )
            })}

          <ChoicePill
            title={filterTexts.panel.changedWithin}
            value={filters.changedWithinDays === undefined ? 'any' : String(filters.changedWithinDays)}
            options={[
              { value: 'any', label: filterTexts.panel.changedAny },
              ...CHANGED_PERIODS.map((days) => ({
                value: String(days),
                label: filterTexts.chips.changedDays(days),
                count: chips.changed[days] ?? 0,
              })),
            ]}
            onPick={(value) => {
              const next = { ...filters }
              if (value === 'any') delete next.changedWithinDays
              else next.changedWithinDays = Number(value)
              setFilters(next)
            }}
          />
        </div>

        <div className="pills__right">
          <span className="pills__shown t-meta">
            {filterTexts.counters.shownLabel(state.visibleBrands.length, state.totalBrands)}
          </span>
          {state.activeCount > 0 ? (
            <button type="button" className="pills__reset" onClick={state.clearAll}>
              {filterTexts.pills.clearAll}
              <IconClose size={16} />
            </button>
          ) : null}
        </div>
      </div>

      <div className="pills__row pills__row--quick">
        <span className="pills__title t-label">{filterTexts.pillGroups.quick}</span>

        <div className="pills__scroll">
          <QuickFlag
            label={filterTexts.chips.noUrl} count={chips.noUrl}
            on={flagChipState(filters, 'hasUrl', false) === 'on'}
            onToggle={() => setFilters(withFlag(filters, 'hasUrl', false, 'toggle'))}
          />
          <QuickFlag
            label={filterTexts.chips.unclear} count={chips.unclear}
            on={flagChipState(filters, 'categoriesUnclear', true) === 'on'}
            onToggle={() => setFilters(withFlag(filters, 'categoriesUnclear', true, 'toggle'))}
          />
          <QuickValue label={filterTexts.pillGroups.noTier} count={chips.tiers[UNSET] ?? 0}
            group="tiers" value={UNSET} state={state} />
          <QuickValue label={filterTexts.pillGroups.noSeo} count={chips.seoPriority[UNSET] ?? 0}
            group="seoPriority" value={UNSET} state={state} />
          <QuickValue label={filterTexts.pillGroups.noContext} count={chips.contextPriority[UNSET] ?? 0}
            group="contextPriority" value={UNSET} state={state} />
          <QuickValue label={filterTexts.pillGroups.hidden} count={chips.hidden}
            group="visibility" value="no" state={state} />
          <QuickFlag
            label={filterTexts.pillGroups.comments} count={chips.comments}
            on={flagChipState(filters, 'hasComments', true) === 'on'}
            onToggle={() => setFilters(withFlag(filters, 'hasComments', true, 'toggle'))}
          />
          <QuickFlag
            label={filterTexts.panel.onlyOneChannel} count={chips.onlyOneChannel}
            on={flagChipState(filters, 'onlyOneChannel', true) === 'on'}
            onToggle={() => setFilters(withFlag(filters, 'onlyOneChannel', true, 'toggle'))}
          />
          <QuickFlag
            label={filterTexts.chips.changed(period)} count={chips.changed[period] ?? 0}
            on={filters.changedWithinDays !== undefined}
            onToggle={() => setFilters({
              ...filters,
              changedWithinDays: filters.changedWithinDays === undefined ? period : undefined,
            })}
          />
          {/* Незакрытая задача — самый частый вопрос к таблице: «где у нас
              висит несделанное». */}
          <QuickFlag
            label={filterTexts.chips.task} count={chips.tasks}
            on={flagChipState(filters, 'hasTask', true) === 'on'}
            onToggle={() => setFilters(withFlag(filters, 'hasTask', true, 'toggle'))}
          />
          <QuickFlag
            label={filterTexts.chips.commented(period)} count={chips.commented?.[period] ?? 0}
            on={filters.commentWithinDays !== undefined}
            onToggle={() => setFilters({
              ...filters,
              commentWithinDays: filters.commentWithinDays === undefined ? period : undefined,
            })}
          />
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────── каркас чипа ────────────────────────────────

function Pill({ title, summary, active, onClear, children }: {
  title: string
  /** Что выбрано — показывается прямо в чипе. */
  summary: ReactNode
  active: boolean
  onClear: () => void
  children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null)

  return (
    <span className={active ? 'pill pill--on' : 'pill'}>
      <button
        ref={setAnchor}
        type="button"
        className="pill__main"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((previous) => !previous)}
      >
        <span className="pill__label">{title}</span>
        {active ? <span className="pill__values">{summary}</span> : null}
        <IconChevronDown size={16} />
      </button>

      {active ? (
        <button
          type="button"
          className="pill__clear"
          aria-label={filterTexts.pillGroups.clearOne(title)}
          onClick={onClear}
        >
          <IconClose size={16} />
        </button>
      ) : null}

      <Popover
        open={open}
        anchor={anchor}
        onClose={() => setOpen(false)}
        role="dialog"
        placement="bottom-start"
        minWidth={240}
        maxWidth={320}
      >
        {children(() => setOpen(false))}
      </Popover>
    </span>
  )
}

// ─────────────────────────── чип значений справочника ────────────────────────

function GroupPill({ spec, options, counts, state }: {
  spec: GroupSpec
  options: { value: string | null; label: string }[]
  counts: Counts
  state: BrandFiltersState
}) {
  const { filters, setFilters } = state
  const picked = options.filter((option) => groupChipState(filters, spec.key, option.value) === 'on')

  return (
    <Pill
      title={spec.title}
      active={picked.length > 0}
      onClear={() => setFilters({ ...filters, [spec.key]: undefined })}
      summary={(
        <>
          {picked.slice(0, 3).map((option) => (
            <ValueBadge key={option.value ?? UNSET} spec={spec} value={option.value} label={option.label} />
          ))}
          {picked.length > 3 ? <span className="pill__more t-meta">+{picked.length - 3}</span> : null}
        </>
      )}
    >
      {() => (
        <ul className="pill-menu" aria-label={spec.title}>
          {options.map((option) => (
            <li key={option.value ?? UNSET} className="pill-menu__row">
              <Checkbox
                checked={groupChipState(filters, spec.key, option.value) === 'on'}
                onChange={() => setFilters(withGroupValue(filters, spec.key, option.value, 'toggle'))}
                label={(
                  <span className="pill-menu__label">
                    <ValueBadge spec={spec} value={option.value} label={option.label} />
                  </span>
                )}
              />
              <span className="pill-menu__count t-meta">{counts[option.value ?? UNSET] ?? 0}</span>
            </li>
          ))}
        </ul>
      )}
    </Pill>
  )
}

/** Плашка значения — ровно та же, что в ячейке таблицы: цвета совпадают.
    Цвет берётся из справочника, поэтому компонент сам лезет в снимок: иначе
    его пришлось бы протаскивать через три уровня чипов. */
function ValueBadge({ spec, value, label }: {
  spec: GroupSpec
  value: string | null
  label: string
}): ReactNode {
  const enums = useApp((state) => state.bootstrap?.enums)
  if (value === UNSET || value === null) return <span className="pill-badge pill-badge--empty">{label}</span>
  if (spec.badge === 'tier') return <TierBadge tier={value} />
  const color = (enums?.[spec.enumSet] ?? []).find((item) => item.value === value)?.color
  return (
    <span className="enum-chip" data-set={spec.enumSet} data-value={value} data-color={chipColor(color)}>
      {label}
    </span>
  )
}

// ──────────────────────────── чип одиночного выбора ──────────────────────────

function ChoicePill({ title, value, options, onPick }: {
  title: string
  value: string
  options: { value: string; label: string; count?: number }[]
  onPick: (value: string) => void
}) {
  const current = options.find((option) => option.value === value)

  return (
    <Pill
      title={title}
      active={value !== 'any'}
      onClear={() => onPick('any')}
      summary={<span className="pill-badge">{current?.label ?? ''}</span>}
    >
      {(close) => (
        <ul className="pill-menu" aria-label={title}>
          {options.map((option) => (
            <li key={option.value} className="pill-menu__row">
              <label className="pill-menu__radio">
                <input
                  type="radio"
                  name={`pill-${title}`}
                  checked={option.value === value}
                  onChange={() => { onPick(option.value); close() }}
                />
                <span>{option.label}</span>
              </label>
              {option.count === undefined ? null : (
                <span className="pill-menu__count t-meta">{option.count}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Pill>
  )
}

// ───────────────────────────── чип с чекбоксами ──────────────────────────────

function ChecksPill({ title, items, onToggle, onClear }: {
  title: string
  items: { id: string; label: string; count: number; on: boolean }[]
  onToggle: (id: string) => void
  onClear: () => void
}) {
  const picked = items.filter((item) => item.on)

  return (
    <Pill
      title={title}
      active={picked.length > 0}
      onClear={onClear}
      summary={(
        <>
          {picked.slice(0, 2).map((item) => (
            <span key={item.id} className="pill-badge">{item.label}</span>
          ))}
          {picked.length > 2 ? <span className="pill__more t-meta">+{picked.length - 2}</span> : null}
        </>
      )}
    >
      {() => (
        <ul className="pill-menu" aria-label={title}>
          {items.length === 0 ? (
            <li className="pill-menu__empty t-meta">{filterTexts.panel.categoriesEmpty}</li>
          ) : null}
          {items.map((item) => (
            <li key={item.id} className="pill-menu__row">
              <Checkbox checked={item.on} onChange={() => onToggle(item.id)} label={item.label} />
              <span className="pill-menu__count t-meta">{item.count}</span>
            </li>
          ))}
        </ul>
      )}
    </Pill>
  )
}

// ─────────────────────────────── чип категорий ───────────────────────────────

/**
 * Категорий 46, поэтому у чипа свой поиск по справочнику и переключатель
 * «любая из · все выбранные»: выбирать из длинного списка без поиска — мучение.
 */
function CategoryPill({ state }: { state: BrandFiltersState }) {
  const categories = useApp((s) => s.bootstrap?.categories ?? [])
  const { filters, chips, setFilters } = state
  const [query, setQuery] = useState('')

  const picked = filters.categoryIds ?? []
  const found = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return categories
      .filter((item) => item.status === 'active')
      .filter((item) => needle === '' || item.name.toLowerCase().includes(needle))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  }, [categories, query])

  const nameOf = (id: number) => categories.find((item) => item.id === id)?.name ?? String(id)

  return (
    <Pill
      title={filterTexts.panel.categories}
      active={picked.length > 0}
      onClear={() => {
        const next = { ...filters }
        delete next.categoryIds
        setFilters(next)
      }}
      summary={(
        <>
          {picked.slice(0, 2).map((id) => (
            <span key={id} className="pill-badge">{nameOf(id)}</span>
          ))}
          {picked.length > 2 ? <span className="pill__more t-meta">+{picked.length - 2}</span> : null}
        </>
      )}
    >
      {() => (
        <div className="pill-cats">
          <div className="pill-cats__search">
            <Input
              value={query}
              size="m"
              iconBefore={<IconSearch size={16} />}
              placeholder={filterTexts.panel.categoriesSearch}
              aria-label={filterTexts.panel.categoriesSearch}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          <ul className="pill-menu" aria-label={filterTexts.panel.categories}>
            {found.length === 0 ? (
              <li className="pill-menu__empty t-meta">{filterTexts.panel.categoriesEmpty}</li>
            ) : null}
            {found.map((category) => (
              <li key={category.id} className="pill-menu__row">
                <Checkbox
                  checked={picked.includes(category.id)}
                  onChange={(checked) => {
                    const next = checked
                      ? [...picked, category.id]
                      : picked.filter((id) => id !== category.id)
                    const value = { ...filters }
                    if (next.length === 0) delete value.categoryIds
                    else value.categoryIds = next
                    setFilters(value)
                  }}
                  label={category.name}
                />
                <span className="pill-menu__count t-meta">{chips.categories[category.id] ?? 0}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Pill>
  )
}

// ───────────────────────────── быстрые фильтры ───────────────────────────────

function QuickFlag({ label, count, on, onToggle }: {
  label: string
  count: number
  on: boolean
  onToggle: () => void
}) {
  return (
    <button type="button" className={on ? 'quick quick--on' : 'quick'} aria-pressed={on} onClick={onToggle}>
      {label}
      <span className="quick__count t-meta">{count}</span>
    </button>
  )
}

/** Быстрый фильтр, который включает одно значение группы (например «без тира»). */
function QuickValue({ label, count, group, value, state }: {
  label: string
  count: number
  group: ValueGroupKey
  value: string | null
  state: BrandFiltersState
}) {
  const { filters, setFilters } = state
  return (
    <QuickFlag
      label={label}
      count={count}
      on={groupChipState(filters, group, value) === 'on'}
      onToggle={() => setFilters(withGroupValue(filters, group, value, 'toggle'))}
    />
  )
}
